import { FC, useCallback, useState } from 'react';
import { isError } from '@belong/api/helpers';
import { DATE_FORMAT, formatDate } from '@belong/date-utils';
import {
  AlertContainer,
  ButtonGroup,
  Card,
  DateOfBirthInput,
  EmailInput,
  FormButton,
  GenericFormLayout,
  Heading,
  LIB_INPUTS_DEFAULT_IDS,
  RichText,
  Stack,
  useForm,
  useStepper
} from '@belong/ui-components';
import { authAPI } from '../../../api/AuthAPI';
import { AUTHENTICATION_FORM_TEST_ID } from '../../../constants';
import { StatusScreen } from '../StatusScreen';
import { IResetFormContent } from '../types';

type Inputs = typeof LIB_INPUTS_DEFAULT_IDS.DOB | typeof LIB_INPUTS_DEFAULT_IDS.EMAIL;

export const ResetForm: FC<IResetFormContent> = ({
  heading,
  introductionText,
  cancelButtonLabel,
  labelReset,
  emailInput,
  dateOfBirthInput,
  sectionInfoTooManyRequests,
  contactUsInfoTooManyRequests
}) => {
  const [code, setCode] = useState<string>('');
  const [responseStatus, setResponseStatus] = useState<number>();
  const { next, prev } = useStepper<{ email: string; dateOfBirth: string }>();
  const { errors, manageForm, FormRef } = useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dictionary, setDictionary] = useState<Record<Inputs, string>>({ dateOfBirth: '', email: '' });

  const onSubmit = useCallback(async () => {
    if (isLoading) {
      return;
    }

    const { email, dateOfBirth } = manageForm.getData<Inputs>();
    setDictionary({ email, dateOfBirth });

    setCode('');
    setIsLoading(true);

    const formattedDob = formatDate(dateOfBirth, DATE_FORMAT.ISO, DATE_FORMAT.INPUT);

    const { body, status } = await authAPI.getResetLink({ email, dateOfBirth: formattedDob });

    if (isError(body)) {
      setResponseStatus(status);
      setCode(body.errorCode);
      setIsLoading(false);
      return;
    }

    next({ email, dateOfBirth });
  }, [manageForm, next]);

  if (responseStatus === 429) {
    return (
      <StatusScreen
        {...dictionary}
        contactUsInfo={contactUsInfoTooManyRequests}
        sectionInfoConfirmation={sectionInfoTooManyRequests}
        testIdSuffix="too-many-requests-screen"
      />
    );
  }

  return (
    <GenericFormLayout
      data-testid={AUTHENTICATION_FORM_TEST_ID.PASSWORD_RESET_LINK}
      FormRef={FormRef}
      handleSubmit={onSubmit}
      isLoading={isLoading}
      validateForm={manageForm.validateForm}
      autoComplete="off"
      useButtonSpinner
    >
      <Card hasSectionPaddingTop>
        <Stack space={{ xs: 'large', md: 'xlarge' }}>
          <Heading variant="large" alignment="center" as="h1">
            {heading}
          </Heading>
          <RichText {...introductionText} alignment="center" />
          {code && (
            <AlertContainer code={code} data-testid={AUTHENTICATION_FORM_TEST_ID.PASSWORD_RESET_LINK_ALERT} hasFocus />
          )}
          <EmailInput
            {...emailInput}
            errorMessages={{ ...emailInput.errorMessages }}
            hasFormError={errors.hasFormError}
            manageForm={manageForm}
            width="full"
          />
          <DateOfBirthInput
            {...dateOfBirthInput}
            errorMessages={{ ...dateOfBirthInput.errorMessages }}
            hasFormError={errors.hasFormError}
            manageForm={manageForm}
            width="full"
          />
          <ButtonGroup hasControl={{ xs: ['column', 'center', false], sm: ['row', 'right', true] }}>
            <FormButton
              type="submit"
              variant="primary"
              width={{
                xs: 'full',
                sm: 'min'
              }}
            >
              {labelReset}
            </FormButton>
            <FormButton
              variant="secondary"
              type="button"
              width={{
                xs: 'full',
                sm: 'min'
              }}
              onClick={() => prev()}
            >
              {cancelButtonLabel}
            </FormButton>
          </ButtonGroup>
        </Stack>
      </Card>
    </GenericFormLayout>
  );
};
